import Phaser from 'phaser';
import BaseGameScene from './BaseGameScene.js';
import { GAME_WIDTH, GAME_HEIGHT, ROCKET_PICKUP_CHANCE } from '../constants.js';
import { logStage, STAGES } from '../utils/IPLogger.js';

// Level 2 specific constants
const LEVEL2_WIDTH = GAME_WIDTH * 3;  // 2400px
const LEVEL2_HEIGHT = GAME_HEIGHT * 3.5; // 2100px - extra space for extraction zone
const C4_TOTAL_CHARGES = 3;
const ESCAPE_TIME = 15; // seconds

/**
 * Level2Scene - Level 2: The Power Plant
 *
 * Mission: Destroy the nuclear power plant powering LAZARUS chambers
 * 1. Get C4 from armory (3 charges)
 * 2. Breach security gate (uses 1 charge)
 * 3. Arm both reactors (uses 2 charges)
 * 4. Escape before meltdown
 *
 * Map: 2400x1800 industrial zone
 */
export default class Level2Scene extends BaseGameScene {
  constructor() {
    super({ key: 'Level2Scene' });
  }

  // ==================== LEVEL CONFIGURATION ====================

  getLevelConfig() {
    return {
      width: LEVEL2_WIDTH,
      height: LEVEL2_HEIGHT,
      initialZombies: 15,
      zombieSpawnDistance: 300,
    };
  }

  getNextLevelScene() {
    return 'AnimatedCreditsScene';
  }

  // ==================== LEVEL-SPECIFIC INIT ====================

  init() {
    super.init();

    // C4 System
    this.c4Charges = 0;
    this.c4MaxCharges = C4_TOTAL_CHARGES;
    this.gateBreached = false;
    this.reactorAArmed = false;
    this.reactorBArmed = false;
    this.escapeActive = false;
    this.escapeTimer = ESCAPE_TIME;
    this.escapeEvent = null;

    // Mission phases
    this.missionPhase = 'armory';
    this.armoryCleared = false;

    // Building references
    this.buildings = null;
    this.interactiveObjects = null;
    this.currentInteraction = null;

    // Gate collision reference
    this.gateCollider = null;
    this.gateColliderRef = null;

    // Perimeter wall references
    this.perimeterWalls = null;
    this.wallColliders = [];
    this.gateSprite = null;

    // Use health property from player
    this.health = 100;

    // Escape speed boost multiplier
    this.escapeSpeedBoost = 1.0;
  }

  // ==================== LEVEL-SPECIFIC CREATE ====================

  create() {
    super.create();

    // Log arrival at level 2
    logStage(STAGES.LEVEL_2_START);

    // Set up action key (E)
    this.actionKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    this.actionKey.on('down', () => this.handleAction());

    // Override player starting position for Level 2 (near armory)
    this.player.setPosition(350, 300);
  }

  // ==================== PLAYER OVERRIDE ====================

  createPlayer() {
    const config = this.getLevelConfig();

    // Start near the armory
    this.player = this.physics.add.sprite(350, 300, 'tank');
    this.player.setScale(0.15);
    this.player.setCollideWorldBounds(true);
    this.player.setDepth(10);
    this.player.body.setSize(200, 200);
    this.player.body.setOffset(53, 121);

    this.player.health = 100;
    this.player.direction = 'up';
    this.player.rotation = 0;
    this.player.isShooting = false;
    this.player.isKnockedBack = false;
    this.player.isInvulnerable = false;
  }

  // ==================== BACKGROUND/MAP ====================

  createBackground() {
    const config = this.getLevelConfig();

    // Fill with dark industrial ground
    const graphics = this.add.graphics();
    graphics.fillStyle(0x2a2a2a, 1);
    graphics.fillRect(0, 0, config.width, config.height);
    graphics.setDepth(-100);

    // Create road network
    this.createRoads();

    // Add props
    this.createProps();
  }

  createRoads() {
    const config = this.getLevelConfig();

    // Main horizontal roads
    const roadY1 = 400;
    const roadY2 = 900;
    const roadY3 = 1500;

    // Draw road areas (darker asphalt)
    const roadGraphics = this.add.graphics();
    roadGraphics.fillStyle(0x333333, 1);
    roadGraphics.setDepth(-95);

    // Horizontal roads
    roadGraphics.fillRect(0, roadY1 - 96, config.width, 192);
    roadGraphics.fillRect(0, roadY2 - 96, config.width, 192);
    roadGraphics.fillRect(0, roadY3 - 96, config.width, 192);

    // Vertical roads
    roadGraphics.fillRect(600 - 96, 0, 192, config.height);
    roadGraphics.fillRect(1800 - 96, 0, 192, 192 + roadY1);

    // Add road markings
    const markingGraphics = this.add.graphics();
    markingGraphics.fillStyle(0xffff00, 0.7);
    markingGraphics.setDepth(-94);

    for (let x = 0; x < config.width; x += 100) {
      markingGraphics.fillRect(x, roadY1, 60, 4);
      markingGraphics.fillRect(x, roadY2, 60, 4);
      markingGraphics.fillRect(x, roadY3, 60, 4);
    }
  }

  createBuildings() {
    // Buildings group
    this.buildings = this.physics.add.staticGroup();
    this.interactiveObjects = [];

    // === ARMORY (Start area - top left) ===
    const armory = this.add.image(300, 200, 'building_armory');
    armory.setScale(0.6);
    armory.setDepth(-80);
    this.armoryZone = new Phaser.Geom.Rectangle(200, 100, 200, 200);
    this.armoryBuilding = armory;

    this.armoryTrigger = this.add.zone(300, 200, 150, 150);
    this.physics.add.existing(this.armoryTrigger, true);
    this.interactiveObjects.push({
      zone: this.armoryTrigger,
      type: 'armory',
      used: false
    });

    // === POWER PLANT PERIMETER WALL SYSTEM ===
    this.createPerimeterWall();

    // === REACTOR A (East) ===
    const reactorA = this.add.image(1400, 1250, 'building_reactor_dome_a');
    reactorA.setScale(0.7);
    reactorA.setDepth(-80);

    this.reactorATrigger = this.add.zone(1400, 1250, 120, 120);
    this.physics.add.existing(this.reactorATrigger, true);
    this.interactiveObjects.push({
      zone: this.reactorATrigger,
      type: 'reactorA',
      used: false
    });

    // === REACTOR B (West) ===
    const reactorB = this.add.image(1000, 1250, 'building_reactor_dome_b');
    reactorB.setScale(0.7);
    reactorB.setDepth(-80);

    this.reactorBTrigger = this.add.zone(1000, 1250, 120, 120);
    this.physics.add.existing(this.reactorBTrigger, true);
    this.interactiveObjects.push({
      zone: this.reactorBTrigger,
      type: 'reactorB',
      used: false
    });

    // === CONTROL BUILDING ===
    const controlBuilding = this.add.image(1200, 1450, 'building_control_room');
    controlBuilding.setScale(0.5);
    controlBuilding.setDepth(-80);

    // === COOLING TOWERS ===
    const coolingTower1 = this.add.image(700, 1150, 'building_cooling_tower');
    coolingTower1.setScale(0.6);
    coolingTower1.setDepth(-80);

    const coolingTower2 = this.add.image(1700, 1150, 'building_cooling_tower');
    coolingTower2.setScale(0.6);
    coolingTower2.setDepth(-80);

    // === FACTORIES AND WAREHOUSES ===
    const factory1 = this.add.image(900, 200, 'building_factory_a');
    factory1.setScale(0.55);
    factory1.setDepth(-80);

    const factory2 = this.add.image(1500, 200, 'building_factory_b');
    factory2.setScale(0.55);
    factory2.setDepth(-80);

    const warehouse1 = this.add.image(300, 600, 'building_warehouse');
    warehouse1.setScale(0.5);
    warehouse1.setDepth(-80);

    const warehouse2 = this.add.image(2100, 600, 'building_warehouse');
    warehouse2.setScale(0.5);
    warehouse2.setDepth(-80);

    // === EXTRACTION ZONE (Far south - must run to escape) ===
    // Position far from the facility for a real escape run
    this.extractionZone = new Phaser.Geom.Rectangle(1050, 1950, 300, 150);
    this.extractionGraphics = this.add.graphics();
    this.extractionGraphics.lineStyle(4, 0x00ff00, 0.5);
    this.extractionGraphics.strokeRect(1050, 1950, 300, 150);
    this.extractionGraphics.setDepth(-60);

    this.extractionText = this.add.text(1200, 1920, 'EXTRACTION ZONE', {
      fontFamily: 'Courier New',
      fontSize: '14px',
      fill: '#00ff00',
    });
    this.extractionText.setOrigin(0.5);
    this.extractionText.setAlpha(0.7);

    // Warning signs
    this.addWarningSign(800, 950, 'DANGER: HIGH VOLTAGE');
    this.addWarningSign(1600, 950, 'NUCLEAR FACILITY');
    this.addWarningSign(1200, 1550, 'AUTHORIZED PERSONNEL ONLY');
  }

  addWarningSign(x, y, text) {
    const sign = this.add.text(x, y, text, {
      fontFamily: 'Courier New',
      fontSize: '10px',
      fill: '#ffff00',
      backgroundColor: '#000000',
      padding: { x: 4, y: 2 },
    });
    sign.setOrigin(0.5);
    sign.setDepth(-50);
  }

  // ==================== PERIMETER WALL SYSTEM ====================

  createPerimeterWall() {
    // Wall configuration - rectangle around power plant complex
    // Power plant area: roughly x: 750-1650, y: 1000-1650
    const wallConfig = {
      left: 700,      // Left edge of perimeter
      right: 1700,    // Right edge of perimeter
      top: 950,       // Top edge of perimeter
      bottom: 1700,   // Bottom edge of perimeter
      wallHeight: 60, // Collision height of wall
      wallScale: 0.12 // Scale for wall sprites
    };

    // Gate position (center of south wall)
    this.gateX = 1200;
    this.gateY = wallConfig.bottom;
    const gateWidth = 200;

    // Create static physics group for walls
    this.perimeterWalls = this.physics.add.staticGroup();

    // --- NORTH WALL (top) ---
    this.createWallSegment(
      wallConfig.left + (wallConfig.right - wallConfig.left) / 2,
      wallConfig.top,
      wallConfig.right - wallConfig.left,
      wallConfig.wallHeight,
      0 // horizontal
    );

    // --- SOUTH WALL (bottom) - split for gate ---
    // Left section (before gate)
    const southLeftWidth = this.gateX - gateWidth/2 - wallConfig.left;
    this.createWallSegment(
      wallConfig.left + southLeftWidth / 2,
      wallConfig.bottom,
      southLeftWidth,
      wallConfig.wallHeight,
      0
    );

    // Right section (after gate)
    const southRightWidth = wallConfig.right - (this.gateX + gateWidth/2);
    this.createWallSegment(
      this.gateX + gateWidth/2 + southRightWidth / 2,
      wallConfig.bottom,
      southRightWidth,
      wallConfig.wallHeight,
      0
    );

    // --- WEST WALL (left) ---
    this.createWallSegment(
      wallConfig.left,
      wallConfig.top + (wallConfig.bottom - wallConfig.top) / 2,
      wallConfig.wallHeight,
      wallConfig.bottom - wallConfig.top,
      1 // vertical
    );

    // --- EAST WALL (right) ---
    this.createWallSegment(
      wallConfig.right,
      wallConfig.top + (wallConfig.bottom - wallConfig.top) / 2,
      wallConfig.wallHeight,
      wallConfig.bottom - wallConfig.top,
      1
    );

    // --- CREATE VISUAL WALL SPRITES ---
    // Add wall sprites along the perimeter for visual effect
    this.createWallVisuals(wallConfig);

    // --- SECURITY GATE ---
    // Gate sprite (closed) - sprite is 828x745, scale to fit ~200px wide gate opening
    this.gateSprite = this.add.image(this.gateX, this.gateY, 'gate_security');
    this.gateSprite.setScale(0.25);
    this.gateSprite.setDepth(-65);
    this.gateSprite.setOrigin(0.5, 0.5);

    // Gate collision zone (blocks player)
    this.gateCollider = this.add.zone(this.gateX, this.gateY, gateWidth, wallConfig.wallHeight);
    this.physics.add.existing(this.gateCollider, true);
    this.gateColliderRef = this.physics.add.collider(this.player, this.gateCollider);

    // Gate interaction zone (for planting C4)
    this.gateTrigger = this.add.zone(this.gateX, this.gateY + 50, gateWidth, 80);
    this.physics.add.existing(this.gateTrigger, true);
    this.interactiveObjects.push({
      zone: this.gateTrigger,
      type: 'gate',
      used: false
    });

    // Spawn guard zombies near gate
    this.time.delayedCall(500, () => {
      this.spawnZombiesAt(this.gateX - 100, this.gateY + 100, 3);
      this.spawnZombiesAt(this.gateX + 100, this.gateY + 100, 3);
    });

    // Add wall collision with player
    this.physics.add.collider(this.player, this.perimeterWalls);
  }

  createWallSegment(x, y, width, height, orientation) {
    // Create invisible collision box
    const wall = this.add.zone(x, y, width, height);
    this.physics.add.existing(wall, true);
    this.perimeterWalls.add(wall);
  }

  createWallVisuals(config) {
    // Wall sprite is 256x64 pixels (top-down concrete wall)
    const wallScale = 0.5; // Scale to ~128x32 in game
    const wallSpacing = 64; // spacing between wall sprites (half the scaled width for overlap)

    // North wall visuals
    for (let x = config.left; x <= config.right; x += wallSpacing) {
      const wallSprite = this.add.image(x, config.top, 'wall_concrete');
      wallSprite.setScale(wallScale);
      wallSprite.setDepth(-70);
    }

    // South wall visuals (skip gate area)
    for (let x = config.left; x <= config.right; x += wallSpacing) {
      // Skip the gate area (wider gap for gate)
      if (x > this.gateX - 120 && x < this.gateX + 120) continue;

      const wallSprite = this.add.image(x, config.bottom, 'wall_concrete');
      wallSprite.setScale(wallScale);
      wallSprite.setDepth(-70);
    }

    // West wall visuals (rotated 90 degrees)
    for (let y = config.top; y <= config.bottom; y += wallSpacing) {
      const wallSprite = this.add.image(config.left, y, 'wall_concrete');
      wallSprite.setScale(wallScale);
      wallSprite.setDepth(-70);
      wallSprite.setAngle(90);
    }

    // East wall visuals (rotated 90 degrees)
    for (let y = config.top; y <= config.bottom; y += wallSpacing) {
      const wallSprite = this.add.image(config.right, y, 'wall_concrete');
      wallSprite.setScale(wallScale);
      wallSprite.setDepth(-70);
      wallSprite.setAngle(90);
    }

    // Corner reinforcement - simple gray squares
    const cornerGraphics = this.add.graphics();
    cornerGraphics.fillStyle(0x666666, 1);
    cornerGraphics.setDepth(-69);

    const cornerSize = 32;
    cornerGraphics.fillRect(config.left - cornerSize/2, config.top - cornerSize/2, cornerSize, cornerSize);
    cornerGraphics.fillRect(config.right - cornerSize/2, config.top - cornerSize/2, cornerSize, cornerSize);
    cornerGraphics.fillRect(config.left - cornerSize/2, config.bottom - cornerSize/2, cornerSize, cornerSize);
    cornerGraphics.fillRect(config.right - cornerSize/2, config.bottom - cornerSize/2, cornerSize, cornerSize);
  }

  createProps() {
    const config = this.getLevelConfig();

    // Burned vehicles
    for (let i = 0; i < 15; i++) {
      const x = Phaser.Math.Between(100, config.width - 100);
      const y = Phaser.Math.Between(100, config.height - 100);

      // Avoid power plant interior
      if (y > 950 && y < 1650 && x > 750 && x < 1650) continue;

      const vehicleKeys = ['vehicle_taxi', 'vehicle_police_0', 'vehicle_police_1', 'vehicle_van'];
      const vehicleKey = vehicleKeys[Phaser.Math.Between(0, vehicleKeys.length - 1)];
      const vehicle = this.add.image(x, y, vehicleKey);
      vehicle.setScale(0.3);
      vehicle.setDepth(-40);
    }

    // Debris
    const debrisKeys = ['debris_dumpster_blue', 'debris_dumpster_yellow', 'debris_trashcan_0', 'debris_hydrant_0', 'debris_mailbox_0', 'debris_newsstand_0'];
    for (let i = 0; i < 30; i++) {
      const x = Phaser.Math.Between(50, config.width - 50);
      const y = Phaser.Math.Between(50, config.height - 50);
      const debrisKey = debrisKeys[Phaser.Math.Between(0, debrisKeys.length - 1)];
      const debris = this.add.image(x, y, debrisKey);
      debris.setScale(0.4);
      debris.setDepth(-45);
      debris.setAlpha(0.8);
    }

    // Barricades near armory
    const barricadeKeys = ['barricade_sandbags_0', 'barricade_sandbags_1', 'barricade_chair_0', 'barricade_concrete_0', 'barricade_fence_0', 'barricade_drum_0'];
    for (let i = 0; i < 5; i++) {
      const x = 200 + i * 60;
      const barricadeKey = barricadeKeys[Phaser.Math.Between(0, barricadeKeys.length - 1)];
      const barricade = this.add.image(x, 350, barricadeKey);
      barricade.setScale(0.4);
      barricade.setDepth(-40);
    }
  }

  // ==================== HUD ====================

  createHUD() {
    const hudStyle = {
      fontFamily: 'Courier New',
      fontSize: '16px',
      fill: '#ffffff',
    };

    // Health
    this.healthText = this.add.text(16, 16, `HEALTH: ${this.player.health}`, hudStyle);
    this.healthText.setScrollFactor(0);
    this.healthText.setDepth(100);

    // Score
    this.scoreText = this.add.text(16, 40, `SCORE: ${this.score}`, hudStyle);
    this.scoreText.setScrollFactor(0);
    this.scoreText.setDepth(100);

    // C4 Charges
    this.c4Text = this.add.text(16, 64, `C4 CHARGES: ${this.c4Charges}/${this.c4MaxCharges}`, {
      ...hudStyle,
      fill: '#ff6600',
    });
    this.c4Text.setScrollFactor(0);
    this.c4Text.setDepth(100);

    // Mission objective
    this.missionText = this.add.text(GAME_WIDTH / 2, 16, 'OBJECTIVE: GET C4 FROM ARMORY', {
      fontFamily: 'Courier New',
      fontSize: '14px',
      fill: '#00ffff',
    });
    this.missionText.setOrigin(0.5, 0);
    this.missionText.setScrollFactor(0);
    this.missionText.setDepth(100);

    // Weapon indicator
    this.weaponText = this.add.text(GAME_WIDTH - 16, 16, 'WEAPON: SHELLS', {
      ...hudStyle,
      fill: '#ffff00',
    });
    this.weaponText.setOrigin(1, 0);
    this.weaponText.setScrollFactor(0);
    this.weaponText.setDepth(100);

    // Rockets remaining
    this.rocketText = this.add.text(GAME_WIDTH - 16, 40, `ROCKETS: ${this.homingRocketsRemaining}`, hudStyle);
    this.rocketText.setOrigin(1, 0);
    this.rocketText.setScrollFactor(0);
    this.rocketText.setDepth(100);

    // Action prompt
    this.actionPrompt = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 50, '', {
      fontFamily: 'Courier New',
      fontSize: '18px',
      fill: '#00ff00',
      backgroundColor: '#000000',
      padding: { x: 10, y: 5 },
    });
    this.actionPrompt.setOrigin(0.5);
    this.actionPrompt.setScrollFactor(0);
    this.actionPrompt.setDepth(100);
    this.actionPrompt.setVisible(false);

    // Escape timer
    this.escapeTimerText = this.add.text(GAME_WIDTH / 2, 80, '', {
      fontFamily: 'Courier New',
      fontSize: '32px',
      fill: '#ff0000',
    });
    this.escapeTimerText.setOrigin(0.5);
    this.escapeTimerText.setScrollFactor(0);
    this.escapeTimerText.setDepth(100);
    this.escapeTimerText.setVisible(false);
  }

  updateHUD() {
    const healthColor = this.player.health > 50 ? '#00ff00' :
                        this.player.health > 25 ? '#ffff00' : '#ff0000';
    this.healthText.setText(`HEALTH: ${this.player.health}`);
    this.healthText.setFill(healthColor);

    this.scoreText.setText(`SCORE: ${this.score}`);
    this.c4Text.setText(`C4 CHARGES: ${this.c4Charges}/${this.c4MaxCharges}`);
    this.rocketText.setText(`ROCKETS: ${this.homingRocketsRemaining}`);
    this.weaponText.setText(`WEAPON: ${this.weaponMode.toUpperCase()}`);
  }

  updateWeaponModeDisplay() {
    // Level 2 uses text-based weapon display
    if (this.weaponText) {
      this.weaponText.setText(`WEAPON: ${this.weaponMode.toUpperCase()}`);
    }
    if (this.rocketText) {
      this.rocketText.setText(`ROCKETS: ${this.homingRocketsRemaining}`);
    }
  }

  // ==================== SPEED BOOST OVERRIDE ====================

  getPlayerSpeed() {
    const baseSpeed = super.getPlayerSpeed();
    return baseSpeed * this.escapeSpeedBoost;
  }

  // ==================== UPDATE OVERRIDE ====================

  update(time) {
    super.update(time);

    // Check for interactive objects
    this.checkInteractions();

    // Check escape sequence
    if (this.escapeActive) {
      this.checkEscapeCondition();
    }
  }

  // ==================== C4 / INTERACTION SYSTEM ====================

  checkInteractions() {
    if (!this.interactiveObjects) return;

    let nearObject = null;

    this.interactiveObjects.forEach((obj) => {
      if (obj.used) return;

      const bounds = obj.zone.getBounds();
      if (bounds.contains(this.player.x, this.player.y)) {
        nearObject = obj;
      }
    });

    // Check extraction zone during escape
    if (this.escapeActive && this.extractionZone.contains(this.player.x, this.player.y)) {
      nearObject = { type: 'extraction' };
    }

    if (nearObject) {
      this.showActionPrompt(nearObject.type);
    } else {
      this.actionPrompt.setVisible(false);
    }

    this.currentInteraction = nearObject;
  }

  showActionPrompt(type) {
    let text = '';

    switch (type) {
      case 'armory':
        if (this.c4Charges === 0) {
          text = 'PRESS [E] TO ACQUIRE C4 EXPLOSIVES';
        }
        break;
      case 'gate':
        if (this.c4Charges > 0 && !this.gateBreached) {
          text = 'PRESS [E] TO PLANT C4 ON GATE';
        } else if (this.c4Charges === 0 && !this.gateBreached) {
          text = 'NEED C4 TO BREACH GATE';
        }
        break;
      case 'reactorA':
        if (this.gateBreached && this.c4Charges > 0 && !this.reactorAArmed) {
          text = 'PRESS [E] TO ARM REACTOR A';
        } else if (!this.gateBreached) {
          text = 'BREACH GATE FIRST';
        }
        break;
      case 'reactorB':
        if (this.gateBreached && this.c4Charges > 0 && !this.reactorBArmed) {
          text = 'PRESS [E] TO ARM REACTOR B';
        } else if (!this.gateBreached) {
          text = 'BREACH GATE FIRST';
        }
        break;
      case 'extraction':
        if (this.escapeActive) {
          text = 'EXTRACTION POINT - GET TO SAFETY!';
        }
        break;
    }

    if (text) {
      this.actionPrompt.setText(text);
      this.actionPrompt.setVisible(true);
    } else {
      this.actionPrompt.setVisible(false);
    }
  }

  handleAction() {
    if (!this.currentInteraction) return;

    const type = this.currentInteraction.type;

    switch (type) {
      case 'armory':
        this.acquireC4();
        break;
      case 'gate':
        this.plantC4OnGate();
        break;
      case 'reactorA':
        this.armReactorA();
        break;
      case 'reactorB':
        this.armReactorB();
        break;
    }
  }

  acquireC4() {
    if (this.c4Charges > 0) return;

    this.c4Charges = C4_TOTAL_CHARGES;
    this.currentInteraction.used = true;
    this.missionPhase = 'gate';

    this.cameras.main.flash(200, 255, 200, 100);
    this.missionText.setText('OBJECTIVE: BREACH POWER PLANT GATE');
    this.showNotification('C4 EXPLOSIVES ACQUIRED (3 CHARGES)');
    this.spawnZombiesAt(300, 200, 5);
  }

  plantC4OnGate() {
    if (this.c4Charges <= 0 || this.gateBreached) return;

    this.c4Charges--;
    this.gateBreached = true;
    this.currentInteraction.used = true;
    this.missionPhase = 'reactors';

    // Explosion effect
    this.cameras.main.shake(500, 0.02);
    this.cameras.main.flash(300, 255, 200, 100);
    this.sound.play('impact', { volume: 0.8 });

    // Switch to intense breach music
    if (this.music) {
      this.music.stop();
    }
    this.music = this.sound.add('level2_breach_music', { loop: true, volume: 0.6 });
    this.music.play();

    // Replace gate sprite with destroyed version
    if (this.gateSprite) {
      this.gateSprite.setTexture('gate_security_destroyed');
    }

    // Remove gate collision (player can now pass through)
    if (this.gateColliderRef) {
      this.gateColliderRef.destroy();
      this.gateColliderRef = null;
    }
    if (this.gateCollider) {
      this.gateCollider.destroy();
      this.gateCollider = null;
    }

    // Add debris particles
    const gateDebrisKeys = ['debris_dumpster_blue', 'debris_trashcan_0', 'debris_hydrant_0', 'debris_mailbox_0'];
    for (let i = 0; i < 8; i++) {
      const debrisX = this.gateX + Phaser.Math.Between(-80, 80);
      const debrisY = this.gateY + Phaser.Math.Between(-40, 40);
      const debrisKey = gateDebrisKeys[Phaser.Math.Between(0, gateDebrisKeys.length - 1)];
      const debris = this.add.image(debrisX, debrisY, debrisKey);
      debris.setScale(0.3);
      debris.setDepth(-60);
      debris.setAlpha(0.8);
    }

    this.missionText.setText('OBJECTIVE: ARM BOTH REACTORS');
    this.showNotification('SECURITY GATE BREACHED!');

    // Spawn zombies inside the facility
    this.spawnZombiesAt(1200, 1200, 8);
  }

  armReactorA() {
    if (this.c4Charges <= 0 || this.reactorAArmed || !this.gateBreached) return;

    this.c4Charges--;
    this.reactorAArmed = true;
    this.currentInteraction.used = true;

    this.cameras.main.flash(200, 255, 100, 100);

    // Pulsing red light
    const light = this.add.circle(1400, 1250, 10, 0xff0000);
    light.setDepth(50);
    this.tweens.add({
      targets: light,
      alpha: { from: 1, to: 0.3 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    });

    this.showNotification('REACTOR A ARMED!');
    this.checkBothReactorsArmed();
  }

  armReactorB() {
    if (this.c4Charges <= 0 || this.reactorBArmed || !this.gateBreached) return;

    this.c4Charges--;
    this.reactorBArmed = true;
    this.currentInteraction.used = true;

    this.cameras.main.flash(200, 255, 100, 100);

    // Pulsing red light
    const light = this.add.circle(1000, 1250, 10, 0xff0000);
    light.setDepth(50);
    this.tweens.add({
      targets: light,
      alpha: { from: 1, to: 0.3 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    });

    this.showNotification('REACTOR B ARMED!');
    this.checkBothReactorsArmed();
  }

  checkBothReactorsArmed() {
    if (this.reactorAArmed && this.reactorBArmed) {
      this.startEscapeSequence();
    } else {
      const remaining = (!this.reactorAArmed ? 'A' : '') + (!this.reactorBArmed ? 'B' : '');
      this.missionText.setText(`ARM REACTOR ${remaining} (${this.c4Charges} C4 LEFT)`);
    }
  }

  startEscapeSequence() {
    this.escapeActive = true;
    this.missionPhase = 'escape';
    this.escapeTimer = ESCAPE_TIME;

    // Adrenaline boost - 50% speed increase during escape
    this.escapeSpeedBoost = 1.5;
    this.showNotification('ADRENALINE SURGE! MOVE FASTER!');

    this.cameras.main.shake(1000, 0.01);
    this.missionText.setText('WARNING: MELTDOWN IMMINENT - ESCAPE NOW!');
    this.missionText.setFill('#ff0000');

    this.escapeTimerText.setVisible(true);
    this.escapeTimerText.setText(`MELTDOWN IN ${this.escapeTimer}`);

    // Countdown timer
    this.escapeEvent = this.time.addEvent({
      delay: 1000,
      callback: () => {
        this.escapeTimer--;
        this.escapeTimerText.setText(`MELTDOWN IN ${this.escapeTimer}`);

        if (this.escapeTimer <= 5) {
          this.cameras.main.flash(100, 255, 0, 0);
        }

        if (this.escapeTimer <= 0) {
          this.gameOver('VAPORIZED BY NUCLEAR EXPLOSION');
        }
      },
      repeat: ESCAPE_TIME - 1,
    });

    // Spawn chase zombies
    this.spawnZombiesAt(this.player.x - 200, this.player.y, 5);
    this.spawnZombiesAt(this.player.x + 200, this.player.y, 5);

    // Flash extraction zone
    this.tweens.add({
      targets: this.extractionGraphics,
      alpha: { from: 1, to: 0.3 },
      duration: 300,
      yoyo: true,
      repeat: -1,
    });

    this.showNotification('GET TO EXTRACTION ZONE!');
  }

  checkEscapeCondition() {
    if (this.extractionZone.contains(this.player.x, this.player.y)) {
      this.levelComplete();
    }
  }

  // ==================== ZOMBIE SPAWNING (Level 2 specific) ====================

  spawnZombiesAt(x, y, count) {
    for (let i = 0; i < count; i++) {
      const offsetX = Phaser.Math.Between(-100, 100);
      const offsetY = Phaser.Math.Between(-100, 100);
      this.createZombie(x + offsetX, y + offsetY);
    }
  }

  // Override createZombie for Level 2 specific settings
  createZombie(x, y) {
    const zombie = this.zombies.create(x, y, 'zombie_shambler_walk');
    zombie.setScale(0.08);
    zombie.setCollideWorldBounds(true);
    zombie.body.setSize(150, 350);
    zombie.body.setOffset(25, 55);
    zombie.play('zombie_walk');
    zombie.markedForDeath = false;
    zombie.isBeingDestroyed = false;
    return zombie;
  }

  // ==================== LEVEL TRANSITIONS ====================

  levelComplete() {
    // Stop escape timer
    if (this.escapeEvent) {
      this.escapeEvent.destroy();
    }

    this.physics.pause();

    // Epic explosion sequence
    this.cameras.main.shake(3000, 0.03);
    this.cameras.main.flash(1000, 255, 200, 100);

    this.escapeTimerText.setVisible(false);

    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 2000,
      });
    }

    const victoryText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, 'POWER PLANT DESTROYED', {
      fontFamily: 'Courier New',
      fontSize: '36px',
      fill: '#00ff00',
    });
    victoryText.setOrigin(0.5);
    victoryText.setScrollFactor(0);
    victoryText.setDepth(300);

    const missionCompleteText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20, 'MISSION COMPLETE', {
      fontFamily: 'Courier New',
      fontSize: '48px',
      fill: '#00ffff',
    });
    missionCompleteText.setOrigin(0.5);
    missionCompleteText.setScrollFactor(0);
    missionCompleteText.setDepth(300);

    this.time.delayedCall(5000, () => {
      if (this.music) this.music.stop();
      this.scene.start('AnimatedCreditsScene');
    });
  }

  gameOver(reason = 'OVERWHELMED BY THE HORDE') {
    // Log death at level 2
    logStage(STAGES.LEVEL_2_DEATH);

    // Stop escape timer
    if (this.escapeEvent) {
      this.escapeEvent.destroy();
    }

    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 2000,
      });
    }

    // Tank destruction effect
    this.player.setTint(0xff0000);
    this.tweens.add({
      targets: this.player,
      rotation: this.player.rotation + Math.PI * 4,
      scale: 0.05,
      alpha: 0.3,
      duration: 1000,
    });

    const gameOverText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, 'GAME OVER', {
      fontFamily: 'Courier New',
      fontSize: '64px',
      fill: '#ff0000',
    });
    gameOverText.setOrigin(0.5);
    gameOverText.setScrollFactor(0);
    gameOverText.setDepth(300);

    const reasonText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 40, reason, {
      fontFamily: 'Courier New',
      fontSize: '18px',
      fill: '#888888',
    });
    reasonText.setOrigin(0.5);
    reasonText.setScrollFactor(0);
    reasonText.setDepth(300);

    this.physics.pause();

    this.time.delayedCall(3000, () => {
      if (this.music) this.music.stop();
      this.scene.start('MenuScene');
    });
  }

  // ==================== CLEANUP ====================

  shutdown() {
    super.shutdown();

    // Clean up escape timer
    if (this.escapeEvent) {
      this.escapeEvent.destroy();
      this.escapeEvent = null;
    }

    // Clean up gate collider
    if (this.gateColliderRef) {
      this.gateColliderRef.destroy();
      this.gateColliderRef = null;
    }
  }
}
